import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { textStyleClass } from '@sirius/typography';

import { AvatarData } from '../_shared/data/avatar.data';
import { ImageData } from '../_shared/models/image/image-data.model';
import { ImageDimensions } from '../_shared/models/image/image-dimensions.model';
import { MediaImage } from '../_shared/models/media/media-image.model';
import { AvatarSize } from '../_shared/types/avatar-size.type';
import { ImageItemComponent } from '../media/media-item/image-item/image-item.component';

@Component({
    selector: 'sirius-avatar',
    templateUrl: './avatar.component.html',
    styleUrls: ['./avatar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ImageItemComponent],
    host: {
        'class': 'sir-avatar',
        '[class]': 'textClass()',
        '[style.--sir-avatar-size]': 'avatarSize()',
        '[attr.role]': 'image() ? null : "img"',
        '[attr.aria-label]': 'image() ? null : accessibleLabel()',
    },
})
export class AvatarComponent {
    readonly firstName = input('');
    readonly lastName = input('');
    readonly size = input<AvatarSize>('xxl');
    readonly image = input<MediaImage | undefined>();
    readonly itemprop = input<string | undefined>();

    readonly initials = computed(() => {
        const firstInitial = this.getInitial(this.firstName());
        const lastInitial = this.getInitial(this.lastName());
        const fallbackInitials = `${firstInitial}${lastInitial}` || this.getInitial(this.avatarLabel());

        if (AvatarData.singleCharacterSizes.has(this.size())) {
            return fallbackInitials.charAt(0);
        }

        return fallbackInitials.slice(0, 2);
    });

    readonly avatarLabel = computed(() =>
        [this.firstName(), this.lastName()].map((name) => name.trim()).filter(Boolean).join(' ')
    );

    readonly accessibleLabel = computed(() => this.avatarLabel() || 'Avatar');

    readonly imageData = computed(() => this.createImageData(this.size()));

    readonly avatarSize = computed(() => `${AvatarData.dimensions[this.size()]}px`);

    /** Typography utility class for the initials (ADR 0001). */
    readonly textClass = computed(() => {
        const { category, size, variant } = AvatarData.textStyle[this.size()];
        return textStyleClass(category, size, variant);
    });

    private getInitial(value: string): string {
        return value.trim().charAt(0).toUpperCase();
    }

    private createImageData(size: AvatarSize): ImageData {
        const dimension = AvatarData.dimensions[size];
        const dimensions: [ImageDimensions, ImageDimensions, ImageDimensions, ImageDimensions, ImageDimensions] = [
            new ImageDimensions(dimension, dimension, '1:1'),
            new ImageDimensions(dimension, dimension, '1:1'),
            new ImageDimensions(dimension, dimension, '1:1'),
            new ImageDimensions(dimension, dimension, '1:1'),
            new ImageDimensions(dimension, dimension, '1:1'),
        ];

        return new ImageData(`avatar-${size}`, dimensions);
    }
}
